import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, filter } from 'rxjs';
import { ApiService } from '../core/http/api.service';
import { InvestorMeta } from '../models/classes/investor-meta.class';
import { UserService } from './user.service';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})

export class InvestorsStorageService {
    private apiService = inject(ApiService);
    private userService = inject(UserService);
    private router = inject(Router);

    private investorsState = signal<InvestorMeta[] | null>(null);
    private activeInvestorState = signal<InvestorMeta | null>(null);
    private activeInvestorIdState = signal<number | null>(null);

    readonly investors = this.investorsState.asReadonly();
    readonly activeInvestor = this.activeInvestorState.asReadonly();
    readonly activeInvestorIdSignal = this.activeInvestorIdState.asReadonly();

    private investors$ = toObservable(this.investorsState);
    private activeInvestor$ = toObservable(this.activeInvestorState);
    private activeInvestorId$ = toObservable(this.activeInvestorIdState);

    public activeInvestorId!: number;

    constructor() {
        // Sync the active investor (what the logo/home links target) to the URL on
        // every navigation. NavigationEnd ensures the route is resolved first.
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => {
                const routeInvestorId = this.getRouteInvestorId();
                if (routeInvestorId && routeInvestorId !== this.activeInvestorId) {
                    this.setActiveInvestor(routeInvestorId);
                }
            });
    }

    initializeInvestorStorage() {
        this.apiService.getInvestors().subscribe((investors: InvestorMeta[]) => {
            this.investorsState.set(investors);
            if (!this.activeInvestorIdState()) {
                const routeInvestorId = this.getRouteInvestorId();
                const cookieInvestorId = this.userService.getCookieValue('selectedInvestorId');
                //check route first then try loading saved investor if still null
                this.activeInvestorId = routeInvestorId || (cookieInvestorId?.trim() ? Number(cookieInvestorId) : null) as unknown as number;
                //all else fails, choose the first investor
                this.activeInvestorId ??= investors[0].id;
                this.setActiveInvestor(this.activeInvestorId);
            } else {
                // Id was already set by navigation; resolve its metadata now.
                this.setActiveInvestor(this.activeInvestorIdState() as number);
            }
        })
    }

    private getRouteInvestorId(): number | null {
        const match = this.router.url.match(/\/investor\/(\d+)/);
        return match ? Number(match[1]) : null;
    }

    setActiveInvestor(id: number): void {
        this.activeInvestorId = id;
        this.activeInvestorIdState.set(id);
        this.activeInvestorState.set(this.investorsState()?.filter(investor => investor.id == id)[0] ?? null);
        this.userService.setCookieValue('selectedInvestorId', id.toString());
    }

    getInvestors(): Observable<InvestorMeta[] | null> {
        return this.investors$;
    }

    getActiveInvestorId(): Observable<number | null> {
        return this.activeInvestorId$;
    }

    getActiveInvestor(): Observable<InvestorMeta | null> {
        return this.activeInvestor$;
    }
}
